import { useCallback, useRef } from 'react';

import type { FetchOptions } from './fetchResponse';
import { fetchResponse } from './fetchResponse';

// Allows using variables from either the mutate call or initial hook call
const resolveVariables = <TVariables>(
	localVariables: TVariables,
	fallbackVariables: TVariables,
): TVariables => {
	return localVariables && Object.keys(localVariables).length > 0
		? localVariables
		: fallbackVariables;
};

export const useFetchData = <TData, TVariables, TResponse>({
	url,
	method,
	variables = {} as TVariables,
	headers,
	body,
	credentials,
	parseResult,
}: FetchOptions<TVariables, TResponse> & {
	variables?: TVariables;
	parseResult: (response: TResponse, variables: TVariables) => TData | Promise<TData>;
}) => {
	// Store props in refs to access the latest values within `useCallback` without adding them to the dependency array
	// This ensures the `fetchData` function remains stable across re-renders
	const latestUrl = useRef(url);
	latestUrl.current = url;

	const latestHeaders = useRef(headers);
	latestHeaders.current = headers;

	const latestBody = useRef(body);
	latestBody.current = body;

	const latestParseResult = useRef(parseResult);
	latestParseResult.current = parseResult;

	const latestVariables = useRef(variables);
	latestVariables.current = variables;

	const latestCredentials = useRef(credentials);
	latestCredentials.current = credentials;

	const fetchData = useCallback(
		async (localVariables = {} as TVariables): Promise<TData> => {
			const resolvedVariables = resolveVariables(localVariables, latestVariables.current);

			const response = await fetchResponse<TVariables, TResponse>(
				{
					url: latestUrl.current,
					method,
					body: latestBody.current,
					headers: latestHeaders.current,
					credentials: latestCredentials.current,
				},
				resolvedVariables,
			);

			return latestParseResult.current(response, resolvedVariables);
		},
		[method],
	);

	return { fetchData };
};

// Fetch multiple endpoints and parse their responses
export const useFetchDataWithMultipleFetches = <TData, TVariables, TResponse>({
	requestConfigs,
	variables = {} as TVariables,
	parseResult,
}: {
	requestConfigs: FetchOptions<TVariables, TResponse>[];
	variables?: TVariables;
	parseResult: (responses: TResponse[], variables: TVariables) => TData | Promise<TData>;
}) => {
	const latestRequestConfigs = useRef(requestConfigs);
	latestRequestConfigs.current = requestConfigs;

	const latestParseResult = useRef(parseResult);
	latestParseResult.current = parseResult;

	const latestVariables = useRef(variables);
	latestVariables.current = variables;

	const fetchData = useCallback(async (localVariables = {} as TVariables): Promise<TData> => {
		const resolvedVariables = resolveVariables(localVariables, latestVariables.current);

		const responses = await Promise.all(
			latestRequestConfigs.current.map((fetchOptions) =>
				fetchResponse<TVariables, TResponse>(fetchOptions, resolvedVariables),
			),
		);

		return latestParseResult.current(responses, resolvedVariables);
	}, []);

	return { fetchData };
};
